/**
 * 6-color e-ink display palette
 * Colors: Black, White, Red, Green, Blue, Yellow
 */

export interface Color {
  r: number
  g: number
  b: number
}

// Palette order MUST match ESP32 (index 0-5 map to BMP palette entries)
export const EINK_PALETTE: Color[] = [
  { r: 0, g: 0, b: 0 },       // 0: Black
  { r: 255, g: 255, b: 255 }, // 1: White
  { r: 255, g: 255, b: 0 },   // 2: Yellow
  { r: 255, g: 0, b: 0 },     // 3: Red
  { r: 0, g: 0, b: 255 },     // 4: Blue
  { r: 0, g: 255, b: 0 },     // 5: Green
]

/**
 * Calculate color distance using weighted Euclidean distance
 * Human eye is more sensitive to green, less to blue
 */
export function colorDistance(c1: Color, c2: Color): number {
  const rMean = (c1.r + c2.r) / 2
  const dr = c1.r - c2.r
  const dg = c1.g - c2.g
  const db = c1.b - c2.b

  // Weighted distance formula for better perceptual accuracy
  const rWeight = 2 + rMean / 256
  const gWeight = 4
  const bWeight = 2 + (255 - rMean) / 256

  return Math.sqrt(rWeight * dr * dr + gWeight * dg * dg + bWeight * db * db)
}

/**
 * Find the closest color in the palette
 */
export function findClosestColor(r: number, g: number, b: number): Color {
  let minDistance = Infinity
  let closest = EINK_PALETTE[0]

  for (const color of EINK_PALETTE) {
    const distance = colorDistance({ r, g, b }, color)
    if (distance < minDistance) {
      minDistance = distance
      closest = color
    }
  }

  return closest
}

/**
 * Get palette index for a color
 */
export function getPaletteIndex(color: Color): number {
  for (let i = 0; i < EINK_PALETTE.length; i++) {
    if (
      EINK_PALETTE[i].r === color.r &&
      EINK_PALETTE[i].g === color.g &&
      EINK_PALETTE[i].b === color.b
    ) {
      return i
    }
  }
  return 0
}
